import { IcEdit, IcPlus as IcPlus24, IcStatusLosange, IcTrash } from '@assets/icons/24x24';
import { IcPlus as IcPlus32 } from '@assets/icons/32x32';
import { ButtonGroup, For, HStack, Icon, IconButton, Text, VStack } from '@chakra-ui/react';
import CockpitDialog from '@components/cockpit/CockpitDialog';
import { CockpitListLayout } from '@components/cockpit/layouts/CockpitListLayout';
import CockpitRow from '@components/cockpit/layouts/CockpitRow';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import useListFormatter from '@hooks/useListFormatter';
import { hasRole, selectActiveGroupOrganization } from '@redux/authent/authent.selectors';
import { selectDenormalizedDrones } from '@redux/utm/utm.selectors';
import { useDeleteDroneMutation } from '@services/utm/drone.api';
import { getClearanceColor } from '@utils/data/notification.utils';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { RoleEnum } from '@/types/authent/roles.types';
import { Drone, DroneClearanceEnum } from '@/types/utm/drone.types';

import { useUtmContext, UtmItemTypeEnum } from '../UtmManagement';

type DronesActionsProps = {
  canCreateDrone: boolean;
  dronesLength: number;
  onCreate: () => void;
};

function DronesActions({ canCreateDrone, dronesLength, onCreate }: Readonly<DronesActionsProps>) {
  return (
    <>
      <Text color="neutral.500">
        <FormattedMessage id="components.utm.drones.result" values={{ count: dronesLength }} />
      </Text>
      {canCreateDrone && (
        <IconButton aria-label="Add" variant="transparent" boxSize="56px" marginLeft="auto" onClick={onCreate}>
          <Icon as={IcPlus24} boxSize="24px" />
        </IconButton>
      )}
    </>
  );
}

function DronesHeader() {
  return (
    <>
      <Text width="220px" color="neutral.500">
        <FormattedMessage id="components.utm.drones.name" />
      </Text>
      <Text width="296px" color="neutral.500">
        <FormattedMessage id="components.utm.drones.collaborativeIds" />
      </Text>
      <Text width="260px" color="neutral.500">
        <FormattedMessage id="components.utm.drones.strategicClearance" />
      </Text>
      <Text width="220px" color="neutral.500">
        <FormattedMessage id="components.utm.drones.model" />
      </Text>
      <Text width="220px" color="neutral.500">
        <FormattedMessage id="components.utm.drones.manufacturer" />
      </Text>
    </>
  );
}

export default function Drones() {
  const { formatMessage } = useIntl();
  const formatList = useListFormatter();
  const drones = useAppSelector(selectDenormalizedDrones);
  const [selectedDrone, setSelectedDrone] = useState<Drone | null>(null);
  const [deleteDrone, { isLoading }] = useDeleteDroneMutation();
  const canEditDrone = useAppSelector((state) => hasRole(state, RoleEnum.EDIT_DRONE));
  const canCreateDrone = useAppSelector((state) => hasRole(state, RoleEnum.CREATE_DRONE));
  const canDeleteDrone = useAppSelector((state) => hasRole(state, RoleEnum.DELETE_DRONE));
  const organization = useAppSelector(selectActiveGroupOrganization);

  const { setEditedItem } = useUtmContext();

  return (
    <CockpitListLayout
      dialog={
        <CockpitDialog
          open={selectedDrone !== null}
          isLoading={isLoading}
          onCancel={() => setSelectedDrone(null)}
          onConfirm={() => {
            if (!selectedDrone) {
              return;
            }
            deleteDrone(selectedDrone.id)
              .unwrap()
              .then(() => {
                createToast(
                  formatMessage(
                    { id: 'components.utm.drones.message.delete' },
                    {
                      name: selectedDrone.functionalName ?? formatMessage({ id: 'components.utm.drones.unknown' }),
                    },
                  ),
                  ToastStatusEnum.SUCCESS,
                );
                setSelectedDrone(null);
              });
          }}
        >
          <Text color="neutral.100" width="100%" whiteSpace="pre-line">
            <FormattedMessage
              id="components.utm.drones.popup.descriptionDelete"
              values={{
                name: selectedDrone?.functionalName ?? formatMessage({ id: 'components.utm.drones.unknown' }),
              }}
            />
          </Text>
        </CockpitDialog>
      }
      actions={
        <DronesActions
          canCreateDrone={canCreateDrone}
          dronesLength={drones.length}
          onCreate={() => setEditedItem({ type: UtmItemTypeEnum.DRONE, value: null })}
        />
      }
      header={drones.length > 0 && <DronesHeader />}
    >
      <For
        each={drones}
        fallback={
          canCreateDrone ? (
            <VStack
              padding={4}
              gap={2}
              marginY="auto"
              className="group"
              border="2px solid"
              cursor="pointer"
              borderColor="neutral.600"
              _hover={{ borderColor: 'skyBlue', backgroundColor: 'neutral.800' }}
              onClick={() => setEditedItem({ type: UtmItemTypeEnum.DRONE, value: null })}
            >
              <Icon as={IcPlus32} boxSize="32px" color="neutral.300" _groupHover={{ color: 'skyBlue' }} />
              <Text>
                <FormattedMessage id="components.utm.drones.createFirst" />
              </Text>
            </VStack>
          ) : undefined
        }
      >
        {(drone, index) => {
          const collaborativeIds = [
            drone.holodroneNumber,
            drone.aeroscopeNumber,
            drone.groundStationNumber,
            drone.skyEyeAntennaNumber,
            drone.adsbNumber,
          ].filter(Boolean) as string[];
          const name = drone.functionalName ?? formatMessage({ id: 'components.utm.drones.unknown' });
          const clearance =
            drone.strategicDrone?.strategicClearance ??
            organization?.organizationParameter.defaultClearance ??
            DroneClearanceEnum.FULL_CLEARANCE_WITHIN_FLIGHTPLAN;
          const isDroneFromClearance = drone.code.startsWith('clearance-');
          return (
            <CockpitRow index={index} listLength={drones.length} key={index}>
              <Tooltip content={drone.functionalName} disabled={!drone.functionalName}>
                <Text width="220px" lineClamp={1}>
                  {name}
                </Text>
              </Tooltip>
              {collaborativeIds.length > 0 ? (
                <Tooltip content={formatList(collaborativeIds)}>
                  <HStack width="296px" gap={1}>
                    <Text
                      paddingX={0.5}
                      backgroundColor="neutral.black"
                      textTransform="uppercase"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      fontWeight="medium"
                      lineHeight="18px"
                      userSelect="none"
                      lineClamp={1}
                    >
                      {collaborativeIds[0]}
                    </Text>
                    {collaborativeIds.length > 1 && (
                      <Text
                        paddingX={0.5}
                        flexShrink={0}
                        backgroundColor="neutral.black"
                        color="blue.500"
                        fontWeight="medium"
                        lineHeight="18px"
                        userSelect="none"
                      >
                        {`+${collaborativeIds.length - 1}`}
                      </Text>
                    )}
                  </HStack>
                </Tooltip>
              ) : (
                <Text width="296px">
                  <FormattedMessage id="global.noInformation" />
                </Text>
              )}
              <HStack gap={1} width="260px">
                <Icon as={IcStatusLosange} boxSize="24px" color={getClearanceColor(clearance)} />
                <Text width="100%">
                  <FormattedMessage id={`clearance.${clearance}`} />
                </Text>
              </HStack>
              <Text width="220px" lineClamp={1}>
                {drone.model}
              </Text>
              <Text width="220px" lineClamp={1}>
                {drone.manufacturer}
              </Text>
              {!isDroneFromClearance && (
                <ButtonGroup marginLeft="auto" attached height="100%">
                  {canEditDrone && (
                    <IconButton
                      variant="transparent"
                      boxSize="56px"
                      aria-label="edit"
                      onClick={() => setEditedItem({ type: UtmItemTypeEnum.DRONE, value: drone })}
                    >
                      <Icon as={IcEdit} boxSize="24px" />
                    </IconButton>
                  )}
                  {canDeleteDrone && (
                    <IconButton
                      variant="transparent"
                      boxSize="56px"
                      aria-label="delete"
                      display="none" //todo
                      onClick={() => setSelectedDrone(drone)}
                    >
                      <Icon as={IcTrash} boxSize="24px" />
                    </IconButton>
                  )}
                </ButtonGroup>
              )}
            </CockpitRow>
          );
        }}
      </For>
    </CockpitListLayout>
  );
}
